import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft, FiLock, FiMail } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { api } from '../services/api';
import { resolveSettingMediaUrl, useSiteSettings } from '../context/SiteSettingsContext';
import './Auth.css';

const Login = () => {
  const { t } = useTranslation();
  const { localePath } = useLocale();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useSiteSettings();
  const from = location.state?.from;

  const handleChange = (event) => {
    setFormData((current) => ({ ...current, [event.target.name]: event.target.value }));
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!formData.email || !formData.password) {
      setError(t('auth.required'));
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post('/api/auth/login', formData);
      if (response.success && response.data?.user && response.data?.token) {
        login(response.data.user, response.data.token);
        navigate(from || (response.data.user.role === 'admin' ? '/admin-panel' : localePath('/user-panel')), { replace: true });
      } else {
        setError(response.message || t('auth.loginFailed'));
      }
    } catch (err) {
      setError(err.data?.message || err.message || t('auth.serverError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="auth-v2-page">
      <section className="auth-v2-shell">
        <div className="auth-v2-card">
            <Link className="auth-v2-brand" to={localePath('/')}>
              <img src={resolveSettingMediaUrl(settings.general.logoUrl) || '/logo/logo-login.webp'} alt={settings.general.siteTitle} />
            </Link>
            <header className="auth-v2-heading">
              <h2>{t('auth.loginTitle')}</h2>
              <p>{t('auth.loginHint')}</p>
            </header>

            {error && <div className="auth-v2-alert is-error" role="alert">{error}</div>}

            <form onSubmit={handleSubmit} className="auth-v2-form">
              <label className="auth-v2-field" htmlFor="email">
                <span>{t('auth.email')}</span>
                <div><FiMail />
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="example@email.com" autoComplete="email" required />
                </div>
              </label>
              <label className="auth-v2-field" htmlFor="password">
                <span>{t('auth.password')}</span>
                <div><FiLock />
                  <input type="password" id="password" name="password" value={formData.password} onChange={handleChange} placeholder={t('auth.passwordPlaceholder')} autoComplete="current-password" required />
                </div>
              </label>
              <div className="auth-v2-options">
                <label className="auth-v2-check"><input type="checkbox" /><span>{t('auth.remember')}</span></label>
                <Link to={localePath('/forgot-password')}>{t('auth.forgotLink')}</Link>
              </div>
              <button type="submit" className="auth-v2-submit" disabled={submitting}>
                {submitting ? t('auth.loggingIn') : t('auth.login')}
              </button>
            </form>

            <footer className="auth-v2-footer">
              {settings.status.registrationEnabled
                ? <p>{t('auth.noAccount')} <Link to={localePath('/register')} state={from ? { from } : undefined}>{t('auth.registerLink')}</Link></p>
                : <p>{t('auth.registrationDisabled')}</p>}
            </footer>
            <Link className="auth-v2-back" to={localePath('/')}><FiArrowLeft /> {t('auth.backSite')}</Link>
        </div>
      </section>
    </main>
  );
};

export default Login;
